import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { ToastModule } from "primeng/toast";
import { ToolbarModule } from "primeng/toolbar";
import { DialogModule } from "primeng/dialog";
import { MessageService } from "primeng/api";
import { forkJoin } from "rxjs";
import { UserService } from "../../services/user.service";
import { AuthenticationService } from "../../services/authentication.service";
import { PermissionEnum } from "../../model/permission-enum";
import { ClubService } from "../../services/club.service";

@Component({
  selector: "app-new-registrations",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    TableModule,
    ButtonModule,
    TagModule,
    ToastModule,
    ToolbarModule,
    DialogModule,
  ],
  providers: [MessageService],
  templateUrl: "./new-registrations.component.html",
  styleUrl: "./new-registrations.component.css",
})
export class NewRegistrationsComponent implements OnInit {
  users: any[] = [];
  selectedUsers: any[] = [];
  accessDenied = false;
  loading = false;
  vipIds: Set<number> = new Set<number>();
  clubId: number | null = null;
  vipDialogVisible = false;
  vipNoteText = "";
  vipUserIds: number[] = [];

  constructor(
    private userService: UserService,
    private authService: AuthenticationService,
    private messageService: MessageService,
    private clubService: ClubService
  ) {}

  ngOnInit(): void {
    const current = this.authService.currentUserValue;
    let permissions = this.authService.currentUserPermissions;
    let hasAdminPermission = permissions.some(
      (permission: PermissionEnum) => permission == PermissionEnum.Admin
    );
    if (!current || !hasAdminPermission) {
      this.accessDenied = true;
      return;
    }

    this.userService.getUser(current.id).subscribe({
      next: (u) => {
        this.clubId = u.club?.id ?? u.club_id ?? null;
        if (this.clubId) {
          this.loadUsers();
        }
      },
      error: () => {
        this.accessDenied = true;
      },
    });
  }

  loadUsers(): void {
    if (!this.clubId) return;
    this.loading = true;
    forkJoin({
      users: this.userService.getUsers(),
      vip: this.clubService.getVipUsers(this.clubId),
    }).subscribe({
      next: ({ users, vip }) => {
        this.vipIds = new Set(vip.map((v: any) => v.id));
        this.users = users.map((u: any) => ({
          ...u,
          isVip: this.vipIds.has(u.id),
          vipNote: vip.find((v: any) => v.id === u.id)?.verified_as_vip_note || "",
        }));
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load users",
        });
        this.loading = false;
      },
    });
  }

  emailSeverity(u: any): "success" | "warning" | "danger" {
    return u.email_verified ? "success" : "danger";
  }

  adminSeverity(u: any): "success" | "warning" | "danger" {
    return u.isVip ? "success" : "warning";
  }

  makeVipSelected(): void {
    const ids = this.selectedUsers
      .filter((u) => !u.isVip)
      .map((u) => u.id);
    if (!ids.length) {
      return;
    }
    this.openVipDialog(ids);
  }

  removeVipSelected(): void {
    if (!this.clubId) return;
    const requests = this.selectedUsers
      .filter((u) => u.isVip)
      .map((u) => this.clubService.removeVipUser(this.clubId!, u.id));
    if (!requests.length) {
      return;
    }
    forkJoin(requests).subscribe({
      next: () => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Users removed from VIP",
        });
        this.loadUsers();
      },
      error: () => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Action failed",
        });
      },
    });
  }

  makeVip(id: number): void {
    this.openVipDialog([id]);
  }

  removeVip(id: number): void {
    if (!this.clubId) return;
    this.clubService.removeVipUser(this.clubId, id).subscribe({
      next: () => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "User removed from VIP",
        });
        this.loadUsers();
      },
      error: () => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Action failed",
        });
      },
    });
  }

  openVipDialog(ids: number[]): void {
    this.vipUserIds = ids;
    this.vipNoteText = "";
    this.vipDialogVisible = true;
  }

  submitVip(): void {
    if (!this.clubId || !this.vipUserIds.length) {
      this.vipDialogVisible = false;
      return;
    }
    const note = this.vipNoteText.trim() || "No VIP note";
    const requests = this.vipUserIds.map((id) =>
      this.clubService.addVipUser(this.clubId!, id, note)
    );
    forkJoin(requests).subscribe({
      next: () => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail:
            this.vipUserIds.length > 1 ? "Users made VIP" : "User made VIP",
        });
        this.loadUsers();
      },
      error: () => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Action failed",
        });
      },
      complete: () => {
        this.vipDialogVisible = false;
        this.vipUserIds = [];
      },
    });
  }

  approveEmail(id: number): void {
    const user = this.users.find((u) => u.id === id);
    if (!user) return;
    this.userService
      .updateUserWithBody({
        ...user,
        phone_number: this.getPhone(id),
        email_verified: true,
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Email approved",
          });
          this.loadUsers();
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Email approval failed",
          });
        },
      });
  }

  getPhone(id: number): string {
    const user = this.users.find((u) => u.id === id);
    return (
      user?.phone_number ||
      user?.phone ||
      user?.phoneNumber ||
      ""
    );
  }
}
